/**
 * Declarative config of `extranet.pendingPiecesListCard` (fiche 22780):
 * the per-piece value descriptors (stable keys `libelle`, `statut`,
 * `lienDossier`, `dateDemande`), the origin → icon / dossier route maps and
 * the texts of the « Pièces à fournir » screen.
 *
 * Keys are the rule contract (`renameField` / `hideField` / `reorderFields`
 * / `setFieldTooltip` target them); labels are display-only.
 */

import { AlertTriangle, FileText, type LucideIcon } from 'lucide-react';

import type { InfoItemDescriptor } from '@components/shared/configurable';

import type { PendingPiece, PendingPieceOrigin } from '../../api/piecesService';
import { extranetPath } from '../../navigation';

/** Registered id of the pending pieces list. */
export const PENDING_PIECES_LIST_CARD_ID = 'extranet.pendingPiecesListCard';

/** Texts of the card and of its page. */
export const PENDING_PIECES_TEXT = {
  /** Section title (`pieces.en-attente`) — overridable through `setTitle`. */
  sectionTitle: 'En attente',
  /** Separator between the values of the secondary line. */
  separator: '·',
  /** Page subtitle « {n} pièces en attente ». */
  pageSubtitle: (count: number): string =>
    `${String(count)} ${count > 1 ? 'pièces' : 'pièce'} en attente`,
} as const;

/** Stable keys of a piece's displayed values. */
export type PendingPieceItemKey = 'libelle' | 'statut' | 'lienDossier' | 'dateDemande';

/** A piece value descriptor (override-able surface + display flags). */
export interface PendingPieceItemField extends InfoItemDescriptor {
  key: PendingPieceItemKey;
  /** `primary` = title line (with the badge), `secondary` = muted line below. */
  line: 'primary' | 'secondary';
  /** `text` = plain value, `badge` = status badge, `link` = dossier link. */
  kind: 'text' | 'badge' | 'link';
  /** Prefix the value with `label` (« Demandé le 13/05/2026 »). */
  showLabel?: boolean;
}

/** Base descriptors (design order: libellé + statut, then dossier · date). */
export const PENDING_PIECE_ITEM_FIELDS: readonly PendingPieceItemField[] = [
  { key: 'libelle', label: 'Pièce', order: 0, line: 'primary', kind: 'text' },
  { key: 'statut', label: 'Statut', order: 1, line: 'primary', kind: 'badge' },
  { key: 'lienDossier', label: 'Dossier concerné', order: 2, line: 'secondary', kind: 'link' },
  {
    key: 'dateDemande',
    label: 'Demandé le',
    order: 3,
    line: 'secondary',
    kind: 'text',
    showLabel: true,
  },
];

/** The view-model value behind each key. */
const VALUE_OF: Readonly<Record<PendingPieceItemKey, (piece: PendingPiece) => string | null>> = {
  libelle: (piece) => piece.libelle,
  statut: (piece) => piece.statut,
  lienDossier: (piece) => piece.reference,
  dateDemande: (piece) => piece.dateDemande,
};

/** The raw (trimmed) value of a descriptor for a piece — `''` when null. */
export function pendingPieceFieldValue(field: PendingPieceItemField, piece: PendingPiece): string {
  return VALUE_OF[field.key](piece)?.trim() ?? '';
}

/** The display text of a descriptor for a piece (with its label prefix). */
export function pendingPieceFieldText(field: PendingPieceItemField, piece: PendingPiece): string {
  const value = pendingPieceFieldValue(field, piece);
  if (value === '' || !field.showLabel || !field.label) return value;
  return `${field.label} ${value}`;
}

/** Sign shown before the dossier reference, per origin. */
export const PENDING_PIECE_ORIGIN_ICON: Readonly<Record<PendingPieceOrigin, LucideIcon>> = {
  POLICE: FileText,
  SINISTRE: AlertTriangle,
};

/** Dossier screen of each origin: contract (`ID_NPOL`) or claim (`ID_SIN`). */
const ORIGIN_ROUTE: Readonly<
  Record<PendingPieceOrigin, { segment: string; idOf: (piece: PendingPiece) => string | null }>
> = {
  POLICE: { segment: 'contrats', idOf: (piece) => piece.idNpol },
  SINISTRE: { segment: 'sinistres', idOf: (piece) => piece.idSin },
};

/**
 * Absolute path of the piece's dossier — `/extranet/{tenant}/contrats/{ID_NPOL}`
 * for a `POLICE` piece, `/extranet/{tenant}/sinistres/{ID_SIN}` for a
 * `SINISTRE` one; `null` when the origin or the target id is missing.
 */
export function pendingPieceDossierPath(
  piece: PendingPiece,
  tenantSlug: string | undefined,
): string | null {
  if (piece.origine === null) return null;
  const route = ORIGIN_ROUTE[piece.origine];
  const id = route.idOf(piece);
  return id === null || id === '' ? null : extranetPath(tenantSlug, route.segment, id);
}
